import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  CannedAnswerEditForm,
  CannedAnswerEditInitialValues,
  CannedAnswerComponents,
  CannedAnswerAdditionalParams,
  CannedAnswerFormOptions,
  onCannedAnswerFormSaved,
  ICannedAnswerFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedCannedAnswer, ISharedPatchCannedAnswer, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchCannedAnswer, patchCannedAnswer, getCannedAnswerUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedCannedAnswer> {}

const CannedAnswerFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchCannedAnswer(id).then(canned_answer => setValue(canned_answer));
  }, [id, setValue, ]);

  const param_props: ICannedAnswerFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && CannedAnswerEditForm(value, param_props, t), [value, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && CannedAnswerFormOptions(value, param_props, t), [value, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchCannedAnswer, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchCannedAnswer) => value && save(() => patchCannedAnswer(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onCannedAnswerFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.CannedAnswer, value)}
    >
      <AtlasForm<ISharedPatchCannedAnswer>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={CannedAnswerComponents}
        additionalParams={CannedAnswerAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={CannedAnswerEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('CANNED_ANSWER')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const CannedAnswerFormEditView = React.memo(withEdit(CannedAnswerFormEdit, getCannedAnswerUpdateSchema));
